import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface TestimonialObject {
  id: number;
  nameEn: string;
  nameAr: string;
  avatar: string;
  contentEn: string;
  contentAr: string;
  likesCount: string;
  okayCount: string;
  isActive: boolean;
  createdAt: string;
  publishDate: string;
  referenceLink: string;
}

export interface TestimonialsListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  testimonials: Array<TestimonialObject>;
  errors: unknown;
}

@Module
export default class TestimonialsModule
  extends VuexModule
  implements TestimonialsListInfo
{
  testimonials = [] as Array<TestimonialObject>;
  allTestimonials = [] as Array<TestimonialObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get testimonials list
   * @returns Array<TestimonialObject>
   */
  get testimonialsList(): Array<TestimonialObject> {
    return this.testimonials;
  }

  /**
   * Get all testimonials list
   * @returns Array<TestimonialObject>
   */
  get allTestimonialsList(): Array<TestimonialObject> {
    return this.allTestimonials;
  }

  /**
   * Get total items
   * @returns number
   */
  get testimonialsListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_TESTIMONIALS](data) {
    this.testimonials = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_TESTIMONIALS](data) {
    this.allTestimonials = data.data;
  }

  @Mutation
  [Mutations.RESET_TESTIMONIALS_LIST]() {
    this.testimonials = [] as Array<TestimonialObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_TESTIMONIALS_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.RESET_TESTIMONIALS_CURRENT_PAGE]() {
    this.testimonials = [] as Array<TestimonialObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.CREATE_TESTIMONIAL](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/testimonials', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_TESTIMONIALS_CURRENT_PAGE);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_TESTIMONIAL](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/testimonials', id)
      .then(() => {
        this.context.commit(Mutations.RESET_TESTIMONIALS_CURRENT_PAGE);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_TESTIMONIAL]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update('/testimonials', id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_TESTIMONIALS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_TESTIMONIALS_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_TESTIMONIALS_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_TESTIMONIAL](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/testimonials', id);
  }

  @Action
  [Actions.GET_TESTIMONIALS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/testimonials', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_TESTIMONIALS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.UPLOAD_TESTIMONIAL_AVATAR]({ id, data }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(`/testimonials/uploadAvatar/${id}`, data)
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.GET_ALL_TESTIMONIALS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/testimonials/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_TESTIMONIALS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }
}
